import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertCard, Button, Table } from "@truevault/aquilo";
import Pagination from "@mui/material/Pagination";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { ApiRouteHelpers } from "../../../../common/root/apiRoutes";
import { ReportGeneratingModal } from "../exports/ReportGeneratingModal";
import { usePrimaryOrganizationId } from "../../../hooks";
import { usePrivacyCenter } from "../../../hooks/usePrivacyCenter";
import { Routes } from "../../../root/routes";
import Flatpickr from "react-flatpickr";
import { Link } from "react-router-dom";
import "flatpickr/dist/themes/light.css";
import "./ConsentRecordFlatpickr.css";

type GpcOptOutRow = {
  id: string;
  eventTimestamp: string | null;
};

type GpcOptOutResponse = {
  data: GpcOptOutRow[];
  meta: {
    page: number;
    per: number;
    totalPages: number;
  };
};

type LatestExportJob = {
  status: string;
  downloadUrl: string | null;
  expiresAt: string | null;
  fromDate: string | null;
  toDate: string | null;
};

const formatExportDate = (isoString: string | null, useUtcDateSpace: boolean = false) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    ...(useUtcDateSpace ? { timeZone: "UTC" } : {}),
  });
};

const dateOnly = (timestamp: string | null) => {
  if (!timestamp) return "-";
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const timeOnly = (timestamp: string | null) => {
  if (!timestamp) return "-";
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const toStartOfDayIso = (date: Date) => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
  ).toISOString();
};

const toEndOfDayIso = (date: Date) => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999),
  ).toISOString();
};

const clampDate = (date: Date, min: Date, max: Date) => {
  if (date < min) return min;
  if (date > max) return max;
  return date;
};

const getDefaultDateRange = () => {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { start, end };
};

const EARLIEST_GPC_DATE = new Date("2026-04-29T00:00:00");

export const GpcOptOutRecords = () => {
  const orgId = usePrimaryOrganizationId();
  const [privacyCenter] = usePrivacyCenter(orgId);
  const [rows, setRows] = useState<GpcOptOutRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [defaultDateRange] = useState(getDefaultDateRange);
  const [startDate, setStartDate] = useState<Date>(defaultDateRange.start);
  const [endDate, setEndDate] = useState<Date | null>(defaultDateRange.end);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [latestExportJob, setLatestExportJob] = useState<LatestExportJob | null>(null);
  const [maxSelectableDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [minSelectableDate] = useState(() => EARLIEST_GPC_DATE);

  const query = useMemo(() => ({ page: page - 1, per: 15 }), [page]);

  useEffect(() => {
    let mounted = true;
    const fetchLatestJob = async () => {
      try {
        const res = await axios.get<LatestExportJob[]>(
          `${ApiRouteHelpers.organizationExports(orgId)}?exportType=GPC_OPT_OUTS&limit=1`,
        );
        if (!mounted) return;
        const job = res.data?.[0] ?? null;
        if (
          job &&
          job.status === "SUCCEEDED" &&
          job.downloadUrl &&
          (!job.expiresAt || new Date(job.expiresAt) > new Date())
        ) {
          setLatestExportJob(job);
        }
      } catch {
        // non-critical, ignore
      }
    };
    void fetchLatestJob();
    return () => {
      mounted = false;
    };
  }, [orgId]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const response = await axios.get<GpcOptOutResponse>(
          ApiRouteHelpers.organizationGpcOptOuts(orgId),
          { params: query },
        );
        if (!mounted) return;
        setRows(response.data.data ?? []);
        setTotalPages(Math.max(response.data.meta?.totalPages ?? 1, 1));
      } catch {
        if (!mounted) return;
        setRows([]);
        setTotalPages(1);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [orgId, query]);

  const handleGenerateReport = async () => {
    const effectiveStartDate = clampDate(startDate, minSelectableDate, maxSelectableDate);
    const effectiveEndDate = clampDate(
      endDate ?? effectiveStartDate,
      minSelectableDate,
      maxSelectableDate,
    );

    setIsGenerating(true);
    try {
      await axios.post(ApiRouteHelpers.organizationExports(orgId), {
        exportType: "GPC_OPT_OUTS",
        filters: {
          fromDate: toStartOfDayIso(effectiveStartDate),
          toDate: toEndOfDayIso(effectiveEndDate),
          privacyCenterPublicId: privacyCenter?.id,
        },
      });
      setShowGenerationModal(true);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="tw-boundary mt-md">
      {showGenerationModal && (
        <ReportGeneratingModal
          reportName="GPC Opt-Out Records"
          onClose={() => setShowGenerationModal(false)}
        />
      )}

      <div className="mb-4 mt-4">
        <div className="rounded-lg border border-[#5331C6] bg-white p-6 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900">
            Generate GPC Opt-Out Records Report
          </h3>
          <p className="mt-1 text-sm text-gray-500">Select timeframe for the records report.</p>
          <div className="mt-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">Date Range</label>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[325px]">
                <Flatpickr
                  className="w-full rounded-md border border-[#5331C6] py-2 pl-3 pr-10 text-sm text-gray-900 focus:border-[#5331C6] focus:outline-none focus:ring-1 focus:ring-[#5331C6]"
                  value={
                    endDate
                      ? [
                          clampDate(startDate, minSelectableDate, maxSelectableDate),
                          clampDate(endDate, minSelectableDate, maxSelectableDate),
                        ]
                      : [clampDate(startDate, minSelectableDate, maxSelectableDate)]
                  }
                  options={{
                    mode: "range",
                    minDate: minSelectableDate,
                    maxDate: maxSelectableDate,
                    dateFormat: "M j, Y",
                  }}
                  onChange={(dates: Date[]) => {
                    if (!dates.length) return;
                    setStartDate(clampDate(dates[0], minSelectableDate, maxSelectableDate));
                    setEndDate(
                      dates.length > 1
                        ? clampDate(dates[1], minSelectableDate, maxSelectableDate)
                        : null,
                    );
                  }}
                />
              </div>
              <Button
                size="s"
                color="secondary"
                onClick={handleGenerateReport}
                disabled={isGenerating}
                customSizeClassname="h-8.5 py-[9px] text-sm leading-[20px]"
              >
                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                  <DocumentTextIcon className="h-5 w-5" />
                  {isGenerating ? "Generating..." : "Generate Report"}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {latestExportJob && (
        <AlertCard
          variant="success"
          title="GPC Opt-Out Records CSV File is available for download"
          description={
            <>
              {latestExportJob.fromDate && latestExportJob.toDate
                ? `For records between ${formatExportDate(
                    latestExportJob.fromDate,
                    true,
                  )} and ${formatExportDate(latestExportJob.toDate, true)}. `
                : ""}
              {latestExportJob.expiresAt
                ? `This file will be available until ${formatExportDate(
                    latestExportJob.expiresAt,
                  )}.`
                : ""}
            </>
          }
          buttonText="Download File"
          buttonPosition="right"
          onButtonClick={() => {
            window.location.href = latestExportJob.downloadUrl!;
          }}
        />
      )}

      <div className="mt-4 rounded-md border border-blue-600 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        Your generated reports are available for download at{" "}
        <Link className="font-medium underline" to={Routes.documents.recordExports}>
          Privacy Docs → Record Exports
        </Link>
        .
      </div>

      <h3 className="text-[24px] font-semibold mb-3 mt-4">Closed GPC Opt-Outs</h3>
      <Table
        data={rows}
        keyExtractor={(item: GpcOptOutRow) => item.id}
        columns={[
          {
            key: "id",
            header: "Request ID",
            render: (item: GpcOptOutRow) => item.id,
            className: "px-3 py-3.5 text-left text-sm text-gray-700",
          },
          {
            key: "timestamp",
            header: "Timestamp",
            render: (item: GpcOptOutRow) => timeOnly(item.eventTimestamp),
            className: "px-3 py-3.5 text-left text-sm text-gray-700",
          },
          {
            key: "date",
            header: "Date",
            render: (item: GpcOptOutRow) => dateOnly(item.eventTimestamp),
            className: "px-3 py-3.5 text-left text-sm text-gray-700",
          },
        ]}
      />

      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            className="[&>ul]:gap-2"
          />
        </div>
      )}
    </div>
  );
};
